import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Gauge,
  Package,
  QrCode,
  Thermometer,
  Weight,
  Wrench,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import DemoBanner from '../../components/optimar/DemoBanner';
import SectionBreak from '../../components/optimar/SectionBreak';
import ProductionStagesTimeline from '../../components/optimar/ProductionStagesTimeline';
import WarningBanner from '../../components/optimar/WarningBanner';
import ResolveWarningDialog from '../../components/optimar/ResolveWarningDialog';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_ID = 'OPT-DEMO-001';

const MetricRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2 last:border-b-0">
    <div className="flex items-center gap-2 text-sm text-slate-600">
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      <span>{label}</span>
    </div>
    <span className={`text-sm font-medium text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const statusPillClass = (status) => {
  switch (status) {
    case 'compliant':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'requires_review':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const OptimarBatchDetail = ({ user }) => {
  const [batch, setBatch] = useState(null);
  const [stages, setStages] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeWarning, setActiveWarning] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, s, m, w, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}`),
        axios.get(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/stages`),
        axios.get(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/metrics`),
        axios.get(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/warnings`),
        axios.get(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/compliance`),
      ]);
      setBatch(b.data);
      setStages(s.data);
      setMetrics(m.data);
      setWarnings(w.data);
      setCompliance(c.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load batch detail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [exporting, setExporting] = useState(null);

  const handleExport = async (kind) => {
    try {
      setExporting(kind);
      const ext = kind === 'pdf' ? 'pdf' : 'json';
      const res = await axios.get(
        `${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/export.${ext}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: kind === 'pdf' ? 'application/pdf' : 'application/json',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${BATCH_ID}-documentation-export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(
        `Demo ${kind.toUpperCase()} export downloaded — not final regulatory documentation.`
      );
    } catch (err) {
      console.error(err);
      toast.error(`Failed to download ${kind.toUpperCase()} export`);
    } finally {
      setExporting(null);
    }
  };

  const openResolveDialog = (warning) => {
    setActiveWarning(warning);
    setDialogOpen(true);
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/reset`);
      toast.success('Demo state reset. Warning is open again.');
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset demo state.');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-slate-600">Loading batch…</p>
        </div>
      </div>
    );
  }

  if (!batch) return null;

  return (
    <div className="space-y-10 sm:space-y-12" data-testid="optimar-batch-detail-page">
      <DemoBanner />

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Package className="h-4 w-4 shrink-0" />
              <span className="font-mono truncate">{batch.batch_id}</span>
            </div>
            <h1 className="mt-1 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl" style={{ fontFamily: 'Manrope' }}>
              {batch.product_name}
            </h1>
            <p className="italic text-slate-600">
              {batch.species} · {batch.production_line}
            </p>
          </div>
          <div className="flex w-full flex-col items-start gap-3 md:w-auto md:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 sm:text-sm">
                Traceability: {batch.traceability_confidence}%
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 sm:text-sm">
                {batch.current_status}
              </div>
            </div>
            <Link
              to="/optimar/customer"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
              data-testid="optimar-open-customer-view"
            >
              <QrCode className="h-4 w-4" /> Open customer / QR view
            </Link>

            <div
              className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white p-3"
              data-testid="optimar-batch-public-qr"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                Public QR Verification
              </p>
              <a
                href={`/verify/${BATCH_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open public verification page"
                data-testid="optimar-batch-public-qr-link"
              >
                <QRCodeSVG
                  value={`${window.location.origin}/verify/${BATCH_ID}`}
                  size={104}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="M"
                  includeMargin={false}
                  data-testid="optimar-batch-public-qr-svg"
                />
              </a>
              <p
                className="max-w-[140px] text-center text-[10px] leading-snug text-slate-500"
                data-testid="optimar-batch-public-qr-disclaimer"
              >
                Demo QR — Optimar-inspired mock data
              </p>
            </div>
          </div>
        </div>
      </div>

      {warnings.length > 0 && (
        <>
          <SectionBreak data-testid="optimar-batch-break-before-warning" />
          <WarningBanner
            warning={warnings[0]}
            onResolveClick={() => openResolveDialog(warnings[0])}
            testIdPrefix="optimar-batch-warning"
          />
        </>
      )}

      <ResolveWarningDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        warning={activeWarning}
        user={user}
        onResolved={() => load()}
      />

      <SectionBreak data-testid="optimar-batch-break-before-timeline" />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
        <div className="order-2 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 lg:order-1 lg:col-span-2">
          <h2 className="mb-4 text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Production stages (11)
          </h2>
          <ProductionStagesTimeline stages={stages} />
        </div>

        <div className="order-1 space-y-6 lg:order-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="mb-3 text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Metrics
            </h2>
            <MetricRow icon={Weight} label="Raw weight" value={`${metrics.raw_weight_kg.toLocaleString()} kg`} />
            <MetricRow icon={Weight} label="Processed weight" value={`${metrics.processed_weight_kg.toLocaleString()} kg`} />
            <MetricRow icon={Gauge} label="Yield percentage" value={`${metrics.yield_percentage}%`} />
            <MetricRow icon={Wrench} label="Discarded product weight" value={`${metrics.discarded_weight_kg.toLocaleString()} kg`} />
            <MetricRow icon={Thermometer} label="Average temperature" value={`${metrics.average_temperature_celsius} °C`} />
            <MetricRow label="Size class" value={metrics.size_class} />
            <MetricRow label="Quality grade" value={metrics.quality_grade} />
            <MetricRow label="Storage location" value={metrics.storage_location} />
            <MetricRow icon={Clock} label="Processing time" value={`${metrics.processing_time_minutes} min`} />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              <FileText className="h-5 w-5 text-slate-500" /> Compliance
            </h2>
            <p className="mb-3 text-xs text-slate-500">
              Reflects data completeness on this demo line. Not a regulatory certification.
            </p>
            <div className="space-y-2">
              {compliance?.items?.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 rounded-lg border border-slate-100 p-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{item.standard}</p>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${statusPillClass(item.status)}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
              <div className="mb-2 flex items-center justify-between gap-2">
                <strong className="text-slate-700">Manual correction log</strong>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600">
                  {compliance?.manual_corrections?.length || 0} entr
                  {(compliance?.manual_corrections?.length || 0) === 1 ? 'y' : 'ies'}
                </span>
              </div>
              {compliance?.manual_corrections?.length ? (
                <ul className="space-y-2" data-testid="optimar-correction-log">
                  {compliance.manual_corrections.map((c) => (
                    <li
                      key={c.id}
                      className="rounded-md border border-emerald-200 bg-white p-2 text-left"
                      data-testid={`optimar-correction-${c.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-emerald-700">{c.id}</span>
                        <span className="text-[11px] text-slate-500">
                          {new Date(c.timestamp).toLocaleString('en-NO')}
                        </span>
                      </div>
                      <p className="mt-1 text-xs">
                        <span className="font-medium text-slate-900">{c.operator}</span>{' '}
                        — <span className="text-slate-600">{c.related_stage}</span>
                      </p>
                      {c.temperature_celsius != null && (
                        <p
                          className="mt-1 text-xs"
                          data-testid={`optimar-correction-${c.id}-temperature`}
                        >
                          <span className="font-medium text-slate-700">Temperature:</span>{' '}
                          <span className="font-mono text-slate-900">
                            {Number(c.temperature_celsius).toFixed(1)} °C
                          </span>
                        </p>
                      )}
                      <p className="mt-1 text-xs text-slate-700">{c.reason}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No corrections recorded yet. Use the warning banner to log one.</p>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="mt-4 w-full"
              onClick={() => undefined}
              asChild
              data-testid="optimar-view-compliance-report"
            >
              <Link to="/optimar/report">
                <FileText className="mr-2 h-4 w-4" />
                View compliance report
              </Link>
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full"
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
              data-testid="optimar-export-pdf-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {exporting === 'pdf'
                ? 'Preparing…'
                : 'Download documentation (PDF)'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full"
              onClick={() => handleExport('json')}
              disabled={exporting !== null}
              data-testid="optimar-export-json-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {exporting === 'json'
                ? 'Preparing…'
                : 'Download documentation (JSON)'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full"
              asChild
              data-testid="optimar-open-public-qr-btn"
            >
              <a
                href={`/verify/${BATCH_ID}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Public QR View
              </a>
            </Button>

            <p
              className="mt-1 text-[10px] leading-snug text-slate-500"
              data-testid="optimar-export-disclaimer"
            >
              Demo export — not final regulatory documentation.
            </p>

            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full text-slate-500 hover:text-slate-700"
              onClick={handleReset}
              disabled={resetting}
              data-testid="optimar-reset-demo"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {resetting ? 'Resetting…' : 'Reset demo state'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimarBatchDetail;
